import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, Loader2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import useAppStore from '../store/useAppStore'
import {
  isPushSupported,
  getNotificationPermission,
  fetchPushConfig,
  subscribePushNotifications,
} from '../services/pushService'

const PROMPT_DISMISSED_AT_KEY = 'skinoz_push_prompt_dismissed_at'
const PROMPT_DONE_KEY = 'skinoz_push_prompt_done'
const REMIND_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000 // напоминание через 7 дней
const SHOW_DELAY_MS = 1500 // небольшая задержка, чтобы не перекрывать загрузку главной

/**
 * Модалка-призыв включить push-уведомления.
 * Показывается на главной после онбординга, если:
 * - окружение поддерживает push (не Telegram Mini App, есть Push API)
 * - фича включена на бэкенде (/api/push/config → enabled)
 * - разрешение ещё не запрашивалось (permission === 'default')
 * - промпт не закрыт навсегда и с последнего «Не сейчас» прошло 7 дней
 *
 * Запрос системного разрешения происходит только по клику «Включить» —
 * это обязательное требование браузеров (см. гайд бэкенда, раздел 10).
 */
const PushPrompt = () => {
  const { t } = useTranslation()
  const isFirstVisit = useAppStore((state) => state.isFirstVisit)
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Промпт только после завершения онбординга
    if (isFirstVisit !== false || !isPushSupported()) {
      return
    }

    // Разрешение уже дано или заблокировано — промпт не нужен
    if (getNotificationPermission() !== 'default') {
      return
    }

    // Пользователь уже включил уведомления или отказался в системном диалоге
    if (localStorage.getItem(PROMPT_DONE_KEY)) {
      return
    }

    // «Не сейчас» — напоминаем не чаще раза в 7 дней
    const dismissedAt = Number(localStorage.getItem(PROMPT_DISMISSED_AT_KEY) || 0)
    if (dismissedAt && Date.now() - dismissedAt < REMIND_INTERVAL_MS) {
      return
    }

    let timerId = null
    let cancelled = false

    // Проверяем, что фича включена на бэкенде, и только тогда показываем
    fetchPushConfig().then((config) => {
      if (cancelled || !config?.enabled) {
        return
      }
      timerId = setTimeout(() => {
        if (!cancelled) {
          setIsVisible(true)
        }
      }, SHOW_DELAY_MS)
    })

    return () => {
      cancelled = true
      if (timerId) {
        clearTimeout(timerId)
      }
    }
  }, [isFirstVisit])

  // Клик «Включить» — системный запрос разрешения внутри обработчика жеста
  const handleEnable = async () => {
    if (isLoading) return

    setIsLoading(true)
    try {
      const subscription = await subscribePushNotifications()

      if (subscription) {
        localStorage.setItem(PROMPT_DONE_KEY, '1')
        setIsVisible(false)
        toast.success(t('profile.pushEnabled'))
        return
      }

      // Отказ в системном диалоге необратим — больше не беспокоим
      if (getNotificationPermission() === 'denied') {
        localStorage.setItem(PROMPT_DONE_KEY, '1')
        setIsVisible(false)
        toast.error(t('profile.pushDeniedHint'), { duration: 5000 })
        return
      }

      // Прочая ошибка (сеть, конфиг) — не спамим, напомним через 7 дней
      localStorage.setItem(PROMPT_DISMISSED_AT_KEY, String(Date.now()))
      setIsVisible(false)
      toast.error(t('profile.pushError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleDismiss = () => {
    if (isLoading) return
    localStorage.setItem(PROMPT_DISMISSED_AT_KEY, String(Date.now()))
    setIsVisible(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6 z-50">
      <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-white border-opacity-10 relative">
        <button
          onClick={handleDismiss}
          disabled={isLoading}
          className="absolute top-4 right-4 text-text-secondary hover:text-white transition-colors disabled:opacity-50"
          aria-label={t('common.close', 'Закрыть')}
        >
          <X size={24} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-full flex items-center justify-center mb-4">
            <Bell size={32} className="text-dark-primary" />
          </div>

          <h3 className="text-xl font-bold text-white mb-2">
            {t('pushPrompt.title')}
          </h3>
          <p className="text-sm text-text-secondary mb-6">
            {t('pushPrompt.text')}
          </p>

          <div className="flex flex-col gap-2 w-full">
            <button
              onClick={handleEnable}
              disabled={isLoading}
              className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Bell size={16} />
              )}
              {t('pushPrompt.enable')}
            </button>
            <button
              onClick={handleDismiss}
              disabled={isLoading}
              className="w-full py-3 bg-transparent border border-text-secondary text-text-secondary rounded-lg hover:bg-text-secondary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('pushPrompt.later')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PushPrompt
